import { useDotLegalSnackbar } from "@dotlegal/dotlegal-ui-components";
import { useState, createElement } from "react";
import { Trans } from "react-i18next";
import { post, put } from "@common/api/apiShared";
import { useValidator } from "@common/hooks/validation";
import { useTranslation } from "@localization/useTranslation";
import { IAddEditLegalEntityTypeDialogProps } from "./AddEditLegalEntityTypeDialog";
import { SaveLegalEntityTypeViewModel } from "../legalEntityTypes/LegalEntityType.types.ts";
import { useDotLegalMutation } from "@common/hooks/dotLegalReactQuery";

export function useAddEditLegalEntityTypeDialogHooks(props: IAddEditLegalEntityTypeDialogProps) {
    const { translateString } = useTranslation();
    const isAdd = props.legalEntityTypeToUpdate === undefined;
    const [saveModel, setSaveModel] = useState<SaveLegalEntityTypeViewModel>({ name: props.legalEntityTypeToUpdate?.name ?? "" });

    const snackBar = useDotLegalSnackbar();

    const validation = useValidator<SaveLegalEntityTypeViewModel>(
        (validators) => ({
            name: validators.validateNotEmpty((item) => item.name, translateString("name")),
        }),
        [saveModel]
    );
    validation.check(saveModel);

    const onModelChange = (updateFunc: (model: SaveLegalEntityTypeViewModel) => void) => {
        let tempSaveModel = { ...saveModel! };

        updateFunc(tempSaveModel);
        setSaveModel(tempSaveModel);
    };

    const saveMutation = useDotLegalMutation(addLegalEntityType, {
        onSuccess: () => {
            snackBar.show(createElement(Trans, { i18nKey: "itemCreatedSuccess", values: { name: saveModel.name } }));
        },
    });

    const updateMutation = useDotLegalMutation(updateLegalEntityType, {
        onSuccess: () => {
            snackBar.show(createElement(Trans, { i18nKey: "itemEditedSuccess", values: { name: saveModel.name } }));
        },
    });

    const onSave = async () => {
        if (validation.anyHasErrors) {
            validation.setShowErrors(true);
            return;
        }

        if (isAdd) {
            await saveMutation.mutateAsync();
        } else {
            await updateMutation.mutateAsync(props.legalEntityTypeToUpdate!.id);
        }

        props.onSaved();
    };

    function addLegalEntityType() {
        return post<string>("/legal-entity-types", saveModel);
    }

    function updateLegalEntityType(id: string) {
        return put<string>(`/legal-entity-types/${id}`, saveModel);
    }

    return {
        isAdd,
        isSaving: saveMutation.isLoading || updateMutation.isLoading,
        validation,
        onModelChange,
        saveModel,
        onSave,
    };
}
